import React from 'react';
import { Box, Input, List, ListItemButton, ListItemText, Theme, Typography } from '@mui/material';

import { Region } from '@paragon/contract-library';
import { createScrollbarStyling } from '@paragon/themes';

const listSx = (theme: Theme) =>
  ({
    bgcolor: 'backContrast.light',
    maxHeight: 150,
    mb: 3,
    overflowX: 'hidden',
    overflowY: 'auto',
    ...createScrollbarStyling(theme),
  }) as const;

function stopPropagation(event: React.MouseEvent<HTMLDivElement>) {
  event.preventDefault();
  event.stopPropagation();
}

interface Props {
  readonly active: readonly Region[];
  readonly available: readonly Region[];
  readonly selected: Region | null;
  readonly onRegionClicked: (region: Region) => void;
}

interface State {
  readonly searchStr: string;
  readonly closed?: boolean;
}

export class StateDropdown extends React.Component<Props, State> {
  state: State = {
    searchStr: '',
  };
  searchInput?: HTMLInputElement | null;

  componentDidUpdate(prevProps: Props, prevState: State) {
    if (!this.state.closed && prevState.closed && this.searchInput) this.searchInput.select();
    else if (!this.state.closed && this.searchInput) this.searchInput.focus();
  }

  onOpen = () => {
    this.setState({ closed: false });
  };

  onClose = () => {
    this.setState({ closed: true });
  };

  onFilterInputChanged = (): React.ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement> => {
    const r = /^[a-zA-Z]{0,2}$/;
    return (event) => {
      const v = event.target.value;
      if (r.test(v)) {
        this.setState({ searchStr: v.toUpperCase() });
      }
    };
  };

  onRegionClicked = (region: Region) => {
    this.props.onRegionClicked(region);
  };

  formatStateItem = (state: Region, index: number) => (
    <ListItemButton
      key={index}
      onClick={() => {
        this.onRegionClicked(state);
      }}
    >
      <ListItemText primary={state.name} />
    </ListItemButton>
  );

  filterStates(stateList: readonly Region[]) {
    const { searchStr } = this.state;
    return stateList.filter((s) => s.abbreviation.substring(0, searchStr.length) === searchStr);
  }

  renderStateListItems = (statesList: readonly Region[]) => {
    if (statesList.length > 0) return statesList.map(this.formatStateItem);
    return (
      <ListItemButton>
        <ListItemText primary="No States Found" />
      </ListItemButton>
    );
  };

  render() {
    const searchBar = (
      <Input
        placeholder="Search..."
        onClick={stopPropagation}
        onChange={this.onFilterInputChanged()}
        sx={{ mb: 2 }}
        value={this.state.searchStr}
        inputProps={{
          'aria-label': 'Description',
        }}
      />
    );

    return (
      <Box sx={{ width: '100%' }}>
        {searchBar}
        <Typography sx={{ mb: 1 }}>{`Registered: ${this.props.active.length} Regions`}</Typography>
        <Box sx={{ width: '100%', bgcolor: 'background.paper' }}>
          <List sx={listSx}>{this.renderStateListItems(this.filterStates(this.props.active))}</List>
        </Box>
        <Typography sx={{ mb: 1 }}>{`Unregistered: ${this.props.available.length} Regions`}</Typography>
        <Box sx={{ width: '100%', bgcolor: 'background.paper' }}>
          <List sx={listSx}>{this.renderStateListItems(this.filterStates(this.props.available))}</List>
        </Box>
      </Box>
    );
  }
}
